/**
 * server-errors.ts — the canonical server validation-error seam (2026-07-12 docket).
 *
 * Every domain-validation failure on the events resource is HTTP 422 with the
 * canonical envelope — byte-identical from @v0y4ge/api-mocks and the real backend:
 *
 *   { "error": { "code", "message", "fields": [{ "path", "code", "message" }], "requestId" } }
 *
 * CD renders what the server returns — no validation logic is invented here.
 * This module owns:
 *   1. parseServerError() — extract the envelope from a thrown api-client error.
 *   2. FIELD_PATH_TO_STEP — static path-prefix → wizard-step map (docket §1).
 *   3. ServerErrors — a tiny store holding the last write's field errors so every
 *      step can render them inline via <FieldError> (wizard-utils), cleared as the
 *      user edits the owning field (store.ts setField hook).
 *
 * NOTE (declared in changelog/2026-07-12T*): the kit's current @v0y4ge/api-client
 * ApiError carries only status/code/message — `body.error` (fields[], requestId)
 * is not yet passed into its existing `details` ctor slot. parseServerError reads
 * `details` when present and degrades to the whole-request banner when not; the
 * one-line client change is Claude Code's (their package).
 */

import { useEffect, useState } from "react";

export interface ServerFieldError {
  /** Dot/bracket JSON path into the request body (e.g. "endsAt", "tickets[0].price"). */
  path: string;
  code: string;
  message: string;
}

export interface ServerErrorEnvelope {
  status?: number;
  code: string;
  message: string;
  fields: ServerFieldError[];
  requestId?: string;
}

/* ── Envelope extraction ───────────────────────────────────────────────────── */

function normalizeFields(raw: unknown): ServerFieldError[] {
  if (!Array.isArray(raw)) return [];
  const out: ServerFieldError[] = [];
  for (const f of raw) {
    if (!f || typeof f !== "object") continue;
    const o = f as Record<string, unknown>;
    const message = typeof o.message === "string" ? o.message : "";
    if (!message) continue; // a field entry without a human message is unrenderable
    out.push({
      path: typeof o.path === "string" ? o.path : "",
      code: typeof o.code === "string" ? o.code : "",
      message,
    });
  }
  return out;
}

/**
 * Parse a thrown api-client error into the canonical envelope, or null when it
 * isn't one (network failure, 5xx, unparseable body) — callers fall back to the
 * generic "nothing was lost" copy for those.
 *
 * Reads the envelope from wherever the client version surfaces it: the ApiError
 * `details` slot (body.error — the declared carrier), a direct `fields` member,
 * or a raw `body.error` — plus status/code/message off the error itself.
 */
export function parseServerError(e: unknown): ServerErrorEnvelope | null {
  if (!e || typeof e !== "object") return null;
  const err = e as Record<string, unknown>;
  const status = typeof err.status === "number" ? err.status : undefined;

  // Candidate objects that may carry {code,message,fields,requestId}, in priority order.
  const candidates: Record<string, unknown>[] = [];
  const push = (v: unknown) => { if (v && typeof v === "object" && !Array.isArray(v)) candidates.push(v as Record<string, unknown>); };
  const details = err.details as Record<string, unknown> | undefined;
  push(details && "error" in details ? details.error : undefined);
  push(details);
  const body = err.body as Record<string, unknown> | undefined;
  push(body && "error" in body ? body.error : undefined);
  push(err);

  let code = "";
  let message = "";
  let requestId: string | undefined;
  let fields: ServerFieldError[] = [];
  for (const c of candidates) {
    if (!code && typeof c.code === "string") code = c.code;
    if (!message && typeof c.message === "string") message = c.message;
    if (!requestId && typeof c.requestId === "string") requestId = c.requestId;
    if (!fields.length) fields = normalizeFields(c.fields);
  }

  // Not a server-validation envelope: no stable code (plain Error / unparseable
  // body → api-client uses 'UNKNOWN'), or a non-4xx status.
  if (!code || code === "UNKNOWN") return null;
  if (status !== undefined && (status < 400 || status >= 500)) return null;

  return { status, code, message: message || "The server rejected this request.", fields, requestId };
}

/* ── path → wizard step (docket §1 FIELD_PATH_TO_STEP, verbatim) ───────────── */

/** Ordered prefix table; match = path.startsWith(prefix). Wildcards in the docket
 * table (location*, tickets*, ticketTypeConfigs*, waitlist*, landing*) are plain
 * prefixes here. */
export const FIELD_PATH_TO_STEP: ReadonlyArray<readonly [string, string]> = [
  ["title", "create.basics"], ["description", "create.basics"], ["tags", "create.basics"],
  ["visibility", "create.details"], ["method", "create.details"], ["location", "create.details"], ["meetingLink", "create.details"],
  ["startsAt", "create.dates"], ["endsAt", "create.dates"], ["timezone", "create.dates"],
  ["ticketTypeIds", "create.tickets"], ["ticketTypeConfigs", "create.tickets"], ["tickets", "create.tickets"],
  ["capacity", "create.tickets"], ["waitlist", "create.tickets"],
  ["slug", "create.landing"], ["landing", "create.landing"],
];

const STEP_LABELS: Record<string, string> = {
  "create.basics": "Essentials",
  "create.details": "Details",
  "create.dates": "Schedule",
  "create.tickets": "Tickets",
  "create.registration": "Registration",
  "create.landing": "Landing page",
  "create.messaging": "Messaging",
};

/** Wizard step that owns a server field path, or null (whole-request / unmapped). */
export function stepForPath(path: string): string | null {
  if (!path) return null;
  for (const [prefix, step] of FIELD_PATH_TO_STEP) {
    if (path === prefix || path.startsWith(prefix)) return step;
  }
  return null;
}

export function stepLabel(route: string): string {
  return STEP_LABELS[route] || route;
}

/* ── ServerErrors store ────────────────────────────────────────────────────── */

let _fields: ServerFieldError[] = [];
let _meta: Omit<ServerErrorEnvelope, "fields"> | null = null;
const _listeners = new Set<() => void>();
const _emit = () => { _listeners.forEach((l) => { try { l(); } catch { /* noop */ } }); };

const matches = (path: string, prefix: string) => path === prefix || path.startsWith(prefix);

/** CreationStore field key → server path prefix(es) it satisfies. Editing that
 * field clears its inline server error (store.ts calls clearForStoreKey). */
const STORE_KEY_TO_PATHS: Record<string, string[]> = {
  title: ["title"], description: ["description"], tags: ["tags"],
  method: ["method"], visibility: ["visibility"],
  venueName: ["location"], address: ["location"], geo: ["location"], locationId: ["location"], locationMode: ["location"], location: ["location"],
  virtualProvider: ["meetingLink", "method"], virtualLink: ["meetingLink"],
  start: ["startsAt"], end: ["endsAt"], timezone: ["timezone"], allDay: ["startsAt", "endsAt"],
  tickets: ["tickets"], ticketTypeIds: ["ticketTypeIds", "tickets"], ticketTypeConfigs: ["ticketTypeConfigs", "tickets"], ticketGroupIds: ["tickets"],
  capacity: ["capacity"], waitlist: ["waitlist"],
  slug: ["slug", "landing"], landingDesign: ["landing"], landingHeadline: ["landing"], landingSubhead: ["landing"], useLanding: ["landing"],
};

export const ServerErrors = {
  /** Replace the current envelope (null clears everything). */
  set(env: ServerErrorEnvelope | null): void {
    _fields = env ? env.fields.slice() : [];
    _meta = env ? { status: env.status, code: env.code, message: env.message, requestId: env.requestId } : null;
    _emit();
  },
  /** Drop errors owned by an edited CreationStore field (user is fixing it). */
  clearForStoreKey(key: string): void {
    const prefixes = STORE_KEY_TO_PATHS[key];
    if (!prefixes || !_fields.length) return;
    const next = _fields.filter((f) => !prefixes.some((p) => matches(f.path, p)));
    if (next.length !== _fields.length) { _fields = next; _emit(); }
  },
  fieldsFor(prefix: string | string[]): ServerFieldError[] {
    const prefixes = Array.isArray(prefix) ? prefix : [prefix];
    return _fields.filter((f) => prefixes.some((p) => matches(f.path, p)));
  },
  all(): ServerFieldError[] { return _fields; },
  meta(): Omit<ServerErrorEnvelope, "fields"> | null { return _meta; },
  subscribe(l: () => void): () => void { _listeners.add(l); return () => { _listeners.delete(l); }; },
};

/** Hook: the server field errors matching a path prefix (or prefixes). */
export function useServerFieldErrors(path: string | string[]): ServerFieldError[] {
  const [, force] = useState(0);
  useEffect(() => ServerErrors.subscribe(() => force((n) => n + 1)), []);
  return ServerErrors.fieldsFor(path);
}
